<template>
  <div class="intervention-time-page container mt-5">
    <div class="text-center mb-4">
      <h1 class="Title mb-0">Temps d'Intervention par Client</h1>
    </div>

    <!-- Liste des clients du mois en cours -->
    <div v-if="!selectedClient" class="clients-list">
      <b-card class="mb-3">
        <div class="d-flex flex-wrap align-items-end gap-2">
          <b-form-group label="Mois" class="mb-0 month-select-group">
            <b-form-select
              v-model="selectedMonth"
              :options="monthOptions"
              @change="fetchClientsCurrentMonth"
            />
          </b-form-group>
          <b-button variant="outline-primary" @click="setCurrentMonth">
            Mois courant
          </b-button>
        </div>
      </b-card>

      <b-card v-if="loading" class="text-center p-5">
        <b-spinner></b-spinner>
        <p class="mt-3">Chargement des données...</p>
      </b-card>

      <b-card v-else-if="clients.length === 0" class="text-center p-5">
        <p class="text-muted">Aucun client avec des interventions pour {{ selectedMonthLabel }}.</p>
      </b-card>

      <div v-else>
        <b-card class="mb-3">
          <h5 class="card-title mb-3">
            <i class="fas fa-calendar-alt me-2"></i>
            Clients avec interventions - {{ selectedMonthLabel }}
          </h5>
          <b-table
            :items="clients"
            :fields="clientFields"
            striped
            hover
            responsive
            @row-clicked="selectClient"
            class="clients-table"
          >
            <template #cell(total_hours)="row">
              <strong>{{ formatHours(row.item.total_hours) }}</strong>
            </template>
            <template #cell(nb_interventions)="row">
              <b-badge variant="info">{{ row.item.nb_interventions }}</b-badge>
            </template>
          </b-table>
        </b-card>
      </div>
    </div>

    <!-- Détail mensuel d'un client -->
    <div v-else class="client-detail">
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            <i class="fas fa-building me-2"></i>
            {{ selectedClient.client_name }}
          </h5>
          <b-button
            variant="outline-secondary"
            @click="selectedClient = null"
            size="sm"
          >
            <i class="fas fa-arrow-left me-2"></i>
            Retour
          </b-button>
        </div>

        <b-card v-if="loadingYearly" class="text-center p-5">
          <b-spinner></b-spinner>
          <p class="mt-3">Chargement du récapitulatif...</p>
        </b-card>

        <div v-else-if="yearlySummary.length === 0" class="text-center p-5">
          <p class="text-muted">Aucune donnée disponible pour ce client.</p>
        </div>

        <div v-else>
          <!-- Graphique simple (barres) -->
          <div class="mb-4">
            <h6 class="mb-3">Évolution mensuelle</h6>
            <div class="chart-container">
              <div
                v-for="(month, index) in yearlySummaryReversed"
                :key="index"
                class="chart-bar"
                :style="{ height: `${(month.total_hours / maxHours) * 100}%` }"
                :title="`${formatMonth(month.month)}: ${formatHours(month.total_hours)}`"
              >
                <div class="chart-bar-value">{{ formatHours(month.total_hours) }}</div>
              </div>
            </div>
            <div class="chart-labels">
              <span
                v-for="(month, index) in yearlySummaryReversed"
                :key="index"
                class="chart-label"
              >
                {{ formatMonthShort(month.month) }}
              </span>
            </div>
          </div>

          <b-table
            :items="yearlySummary"
            :fields="yearlyFields"
            striped
            hover
            responsive
            class="yearly-table"
          >
            <template #cell(month)="row">
              <strong>{{ formatMonth(row.item.month) }}</strong>
            </template>
            <template #cell(total_hours)="row">
              <strong>{{ formatHours(row.item.total_hours) }}</strong>
            </template>
            <template #cell(nb_interventions)="row">
              <b-badge variant="info">{{ row.item.nb_interventions }}</b-badge>
            </template>
          </b-table>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import api from "@/plugins/api.js";

export default {
  name: "InterventionTimePage",
  data() {
    return {
      loading: false,
      loadingYearly: false,
      clients: [],
      selectedClient: null,
      yearlySummary: [],
      selectedMonth: '',
      clientFields: [
        { key: 'client_name', label: 'Client', sortable: true },
        { key: 'total_hours', label: 'Heures', sortable: true },
        { key: 'nb_interventions', label: 'Interventions', sortable: true }
      ],
      yearlyFields: [
        { key: 'month', label: 'Mois', sortable: true },
        { key: 'total_hours', label: 'Heures', sortable: true },
        { key: 'nb_interventions', label: 'Interventions', sortable: true }
      ]
    };
  },
  computed: {
    selectedMonthLabel() {
      if (!this.selectedMonth) return '';
      const [year, month] = this.selectedMonth.split('-').map(Number);
      if (!year || !month) return this.selectedMonth;
      const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ];
      return `${months[month - 1]} ${year}`;
    },
    monthOptions() {
      const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ];
      const options = [];
      const now = new Date();

      for (let i = 0; i < 24; i += 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const value = `${year}-${String(month).padStart(2, '0')}`;
        options.push({
          value,
          text: `${months[month - 1]} ${year}`
        });
      }

      return options;
    },
    maxHours() {
      if (this.yearlySummary.length === 0) return 1;
      return Math.max(...this.yearlySummary.map(m => parseFloat(m.total_hours) || 0), 1);
    },
    yearlySummaryReversed() {
      // Inverser l'ordre pour le graphique : du plus ancien au plus récent (gauche à droite)
      return [...this.yearlySummary].reverse();
    }
  },
  async mounted() {
    this.selectedMonth = this.getCurrentMonthValue();
    await this.fetchClientsCurrentMonth();
  },
  methods: {
    getCurrentMonthValue() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    },
    async setCurrentMonth() {
      this.selectedMonth = this.getCurrentMonthValue();
      await this.fetchClientsCurrentMonth();
    },
    async fetchClientsCurrentMonth() {
      this.loading = true;
      try {
        const config = {};
        if (this.selectedMonth) {
          config.headers = {
            'x-month': this.selectedMonth
          };
        }
        const response = await api.get('/intervention-time/clients-current-month', config);
        this.clients = response.data;
      } catch (error) {
        console.error('Erreur lors du chargement des clients:', error);
        this.$bvToast.toast('Erreur lors du chargement des données', {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    async selectClient(client) {
      this.selectedClient = client;
      this.loadingYearly = true;
      try {
        const response = await api.get(`/intervention-time/client/${client.contact_id}/yearly`);
        this.yearlySummary = response.data;
      } catch (error) {
        console.error('Erreur lors du chargement du récapitulatif:', error);
        this.$bvToast.toast('Erreur lors du chargement du récapitulatif', {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loadingYearly = false;
      }
    },
    formatHours(hours) {
      if (!hours && hours !== 0) return '0h';
      const h = parseFloat(hours);
      if (isNaN(h)) return '0h';
      const hoursInt = Math.floor(h);
      const minutes = Math.round((h - hoursInt) * 60);
      if (hoursInt === 0 && minutes === 0) return '0h';
      if (hoursInt === 0) return `${minutes}min`;
      if (minutes === 0) return `${hoursInt}h`;
      return `${hoursInt}h${minutes.toString().padStart(2, '0')}`;
    },
    formatMonth(monthStr) {
      if (!monthStr) return '';
      const date = new Date(monthStr);
      const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ];
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    },
    formatMonthShort(monthStr) {
      if (!monthStr) return '';
      const date = new Date(monthStr);
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      return `${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;
    }
  }
};
</script>

<style scoped>
.intervention-time-page {
  max-width: 1400px;
}

.month-select-group {
  min-width: 220px;
}

.clients-table {
  cursor: pointer;
}

.clients-table tbody tr:hover {
  background-color: #f0f0f0;
}

/* Éviter que le titre et le bouton se superposent (mobile et PC) */
.client-detail .d-flex {
  flex-wrap: wrap;
  gap: 0.5rem;
  position: relative;
  padding-right: 0;
}

.client-detail h5 {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  margin: 0;
  padding: 0;
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
}

.client-detail .btn {
  flex-shrink: 0;
  margin-left: auto;
  position: relative;
  z-index: 1;
}

/* Sur mobile : réduire la taille de police */
@media (max-width: 768px) {
  .client-detail h5 {
    font-size: 1rem;
  }
}

.stat-box {
  text-align: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #007bff;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

.chart-container {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 200px;
  margin-bottom: 1rem;
  padding: 0 1rem;
}

.chart-bar {
  flex: 1;
  margin: 0 2px;
  background-color: #007bff;
  border-radius: 4px 4px 0 0;
  min-height: 20px;
  position: relative;
  transition: background-color 0.3s;
}

.chart-bar:hover {
  background-color: #0056b3;
}

.chart-bar-value {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  font-weight: bold;
  white-space: nowrap;
}

.chart-labels {
  display: flex;
  justify-content: space-around;
  padding: 0 1rem;
}

.chart-label {
  flex: 1;
  text-align: center;
  font-size: 0.75rem;
  color: #6c757d;
}

@media (max-width: 768px) {
  .chart-container {
    height: 150px;
  }
  
  .chart-bar-value {
    font-size: 0.65rem;
    top: -20px;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
}
</style>

